import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormAnalytics, useComponentAnalytics } from '../hooks/useAnalytics';
import axios from 'axios';
import toast from 'react-hot-toast';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const formAnalytics = useFormAnalytics('contact_form');
  
  // Track component usage
  useComponentAnalytics('ContactPage');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    category: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
        // Track successful form submission
      formAnalytics.trackFormSubmit(true, undefined, {
        category: formData.category,
        has_subject: !!formData.subject,
        message_length: formData.message.length
      });
      const response = await axios.post('/api/contact', formData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success(t('contact.success.message', 'Your message has been sent successfully!'));
        formAnalytics.trackFormSubmit(false, 'Submission failed');

        
        // Reset form data
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          category: ''
        });
      } else {
        throw new Error(response.data.error || 'Failed to send message');
      }
    } catch (error: any) {
      console.error('Error submitting contact form:', error);
      
      if (error.response?.status === 429) {
        toast.error(t('contact.error.rateLimited', 'Too many submissions. Please try again later.'));
      } else if (error.response?.data?.details) {
        // Validation errors
        const validationErrors = error.response.data.details.map((err: any) => err.msg).join(', ');
        toast.error(t('contact.error.validation', `Validation error: ${validationErrors}`));
      } else {
        toast.error(t('contact.error.general', 'Failed to send message. Please try again.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('contact.success.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('contact.success.message')}
            </p>
            <button
              onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  name: '',
                  email: '',
                  subject: '',
                  message: '',
                  category: ''
                });
              }}
              className="bg-cyan-600 text-white px-6 py-2 rounded-md hover:bg-cyan-700"
            >
              {t('contact.success.sendAnother')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                {t('contact.info.title')}
              </h2>
              
              <div className="space-y-6">
                <div>
                  <div className="flex items-center mb-2">
                    <svg className="h-5 w-5 text-cyan-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900">
                      {t('contact.info.email.title')}
                    </h3>
                  </div>
                  <p className="text-gray-700 ml-8">support@tvia.ai</p>
                  <p className="text-gray-500 text-sm ml-8">
                    {t('contact.info.email.description')}
                  </p>
                </div>

                <div>
                  <div className="flex items-center mb-2">
                    <svg className="h-5 w-5 text-cyan-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900">
                      {t('contact.info.phone.title')}
                    </h3>
                  </div>
                  <p className="text-gray-700 ml-8">+1 (555) 123-4567</p>
                  <p className="text-gray-500 text-sm ml-8">
                    {t('contact.info.phone.description')}
                  </p>
                </div>

                <div>
                  <div className="flex items-center mb-2">
                    <svg className="h-5 w-5 text-cyan-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900">
                      {t('contact.info.address.title')}
                    </h3>
                  </div>
                  <p className="text-gray-700 ml-8">
                    {t('contact.info.address.line1')}<br />
                    {t('contact.info.address.line2')}
                  </p>
                </div>

                <div>
                  <div className="flex items-center mb-2">
                    <svg className="h-5 w-5 text-cyan-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900">
                      {t('contact.info.hours.title')}
                    </h3>
                  </div>
                  <p className="text-gray-700 ml-8">
                    {t('contact.info.hours.weekdays')}<br />
                    {t('contact.info.hours.weekends')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                {t('contact.form.title')}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.name')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder={t('contact.form.namePlaceholder')}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder={t('contact.form.emailPlaceholder')}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.category')} *
                  </label>
                  <select
                    id="category"
                    name="category"
                    required
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    <option value="">{t('contact.form.selectCategory')}</option>
                    <option value="general">{t('contact.form.categories.general')}</option>
                    <option value="technical">{t('contact.form.categories.technical')}</option>
                    <option value="billing">{t('contact.form.categories.billing')}</option>
                    <option value="legal">{t('contact.form.categories.legal')}</option>
                    <option value="partnership">{t('contact.form.categories.partnership')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.subject')} *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('contact.form.subjectPlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.message')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('contact.form.messagePlaceholder')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-cyan-600 text-white px-6 py-3 rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('contact.form.sending') : t('contact.form.send')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;